import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { logError } from '@core/Utils/logging/logging'
import { getStartupParameters } from '@core/Areas/Kambi/config/startupParameters'
import customerSettings from '@core/Areas/Kambi/config/customerSettings'
import widgetSettings from '@core/Areas/Kambi/config/widgetSettings'
import { startListening } from '@core/Store/listenerMiddleware'
import { state$ as userAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import {
  isUserResidentOfSA,
  isUserAccountStatePending,
  waitForWapi,
  toggleKambiVisiblity,
  setInitialVisibilityState,
  kambiLog,
  handleKambiLogin,
  handleKambiLogout,
  isKambiSupportedBrowser,
} from '@core/Areas/Kambi/helpers'
import { registerKambiCustomWidgets } from '@core/Areas/Kambi/widgets/register'
import { queryClient } from '@core/Data/ReactQuery'
import { serverConfigQuery } from '@core/Data/ServerConfig/query'
import {
  setIsReloadPending,
  setKambiVisibility,
  setUnsupportedBrowser,
} from '@core/Areas/Kambi/Store'
import { getIsInSignUp } from '@core/Areas/Kambi/Store/selectors'
import { BootstrapJurisdiction, handleScriptLoading } from './helpers/handleScriptLoading'
import { handleClientSubscriptions } from './helpers/handleClientSubscriptions'

let hasLoadedScript = false

export const useKambiClient = (): void => {
  const dispatch = useDispatch()
  const isInSignUp = useSelector(getIsInSignUp)

  const bootstrapJurisdictionRef = React.useRef<BootstrapJurisdiction>()

  // ============
  // Load Scripts
  // ============
  React.useEffect(() => {
    if (hasLoadedScript || bootstrapJurisdictionRef.current) return

    if (!isKambiSupportedBrowser()) {
      dispatch(setUnsupportedBrowser())
      return
    }

    hasLoadedScript = true

    queryClient.fetchQuery(serverConfigQuery).then(({ serverEnvironment }) => {
      window._kc = getStartupParameters(serverEnvironment)
      window.customerSettings = customerSettings
      window.widgetSettings = widgetSettings

      registerKambiCustomWidgets()

      handleScriptLoading()
        .then(bootstrapJurisdiction => {
          bootstrapJurisdictionRef.current = bootstrapJurisdiction
        })
        .catch(() => {
          logError('Error loading Kambi scripts', true)
        })
    })
  }, [dispatch])

  // ==================================
  // Register WAPI when widget is ready
  // ==================================
  React.useEffect(() => {
    if (window.wapi) return

    waitForWapi().then(wapi => {
      // Set initial betslip state minimized + sets up subscription
      wapi.set(wapi.BETSLIP_MAXIMIZED, { maximized: false })
      wapi.request(wapi.USER_DATA)

      setInitialVisibilityState(wapi)

      wapi.subscribe(response => {
        kambiLog('client event', response)
        handleClientSubscriptions(response)
      })
    })
  }, [])

  // ================================================
  // Handle Authentication and Bootstrap Jurisdiction
  // ================================================
  React.useEffect(() => {
    let shouldTryLogout: boolean

    const subscription = userAccountState$
      .skipWhile(isUserAccountStatePending)
      .map(userAccountState => ({
        accountNumber: userAccountState.accountNumber,
        isLoggedIn: userAccountState.isLoggedIn,
        residentialDetail: userAccountState.residentialDetail,
      }))
      .distinctUntilChanged(
        s => s,
        (a, b) => JSON.stringify(a) === JSON.stringify(b)
      )
      .subscribe(({ residentialDetail, isLoggedIn, accountNumber }) => {
        shouldTryLogout = !!isLoggedIn

        if (isLoggedIn && accountNumber && residentialDetail)
          handleKambiLogin(accountNumber, residentialDetail)
        else handleKambiLogout()

        if (!bootstrapJurisdictionRef.current) return

        const requiredBootstrap: BootstrapJurisdiction = isUserResidentOfSA(residentialDetail)
          ? 'SA'
          : 'WA'

        if (requiredBootstrap !== bootstrapJurisdictionRef.current) {
          if (isInSignUp) {
            dispatch(setIsReloadPending(true)) //reload after signup is finished
          } else {
            window.location.reload()
          }
        }
      })

    return () => {
      if (shouldTryLogout) handleKambiLogout()
      subscription.dispose()
    }
  }, [dispatch, isInSignUp])

  // =======================
  // Visibility Subscription
  // =======================
  React.useEffect(() => {
    const subscription = startListening({
      actionCreator: setKambiVisibility,
      effect: action => toggleKambiVisiblity(!!action.payload),
    })

    return () => subscription()
  }, [])
}
